import { useState, useEffect } from 'react';
import { mockOffices, mockBrokerUsers } from '../../services/mockData';

const BrokerOfficesPage = () => {
  const [offices, setOffices] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nmls: '',
    address: '',
    phone: '',
    isMain: false
  });

  useEffect(() => {
    setTimeout(() => {
      setOffices(mockOffices.filter(o => o.brokerId === 1));
      setUsers(mockBrokerUsers);
      setLoading(false);
    }, 500);
  }, []);

  const handleAddOffice = () => {
    const newOffice = {
      id: Math.max(...offices.map(o => o.id)) + 1,
      brokerId: 1,
      name: formData.name,
      nmls: formData.nmls,
      isMain: formData.isMain,
      address: formData.address,
      phone: formData.phone,
      compensation: {
        percentage: 2.0,
        flatFee: 500,
        minComp: 1500,
        maxComp: 12000
      }
    };
    setOffices([...offices, newOffice]);
    setShowAddModal(false);
    resetForm();
  };

  const handleEditOffice = () => {
    setOffices(offices.map(o => 
      o.id === selectedOffice.id
        ? {
            ...o,
            name: formData.name,
            nmls: formData.nmls,
            isMain: formData.isMain,
            address: formData.address,
            phone: formData.phone
          }
        : o
    ));
    setShowEditModal(false);
    setSelectedOffice(null);
    resetForm();
  };

  const openEditModal = (office) => {
    setSelectedOffice(office);
    setFormData({
      name: office.name,
      nmls: office.nmls,
      address: office.address,
      phone: office.phone,
      isMain: office.isMain
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      nmls: '',
      address: '',
      phone: '',
      isMain: false
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getOfficeUsers = (officeId) => {
    return users.filter(u => u.officeId === officeId);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const mainOffice = offices.find(o => o.isMain);
  const hasMainOffice = !!mainOffice;

  return (
    <div className="page-header d-print-none">
      <div className="row g-2 align-items-center mb-3">
        <div className="col">
          <h2 className="page-title">Offices Management</h2>
          <div className="text-secondary mt-1">Manage main and satellite offices</div>
        </div>
        <div className="col-auto ms-auto">
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <i className="ti ti-plus me-2"></i>
            Add Office
          </button>
        </div>
      </div>

      {!hasMainOffice && (
        <div className="alert alert-warning mb-3">
          <div className="d-flex">
            <div>
              <i className="ti ti-alert-triangle me-2"></i>
            </div>
            <div>
              <h4 className="alert-title">Main Office Required</h4>
              <div className="text-secondary">
                You must designate one office as your main office. This is typically your headquarters location.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row row-cards">
        {offices.map((office) => {
          const officeUsers = getOfficeUsers(office.id);
          return (
            <div key={office.id} className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">
                    {office.name}
                    {office.isMain && (
                      <span className="badge bg-blue-lt ms-2">Main Office</span>
                    )}
                </h3>
              </div>
              <div className="card-body">
                <div className="datagrid">
                  <div className="datagrid-item">
                    <div className="datagrid-title">NMLS</div>
                    <div className="datagrid-content">
                      <span className="badge bg-blue-lt">{office.nmls}</span>
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Address</div>
                    <div className="datagrid-content">{office.address}</div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Phone</div>
                    <div className="datagrid-content">{office.phone}</div>
                  </div>
                </div>

                <hr className="my-3" />

                <h4 className="card-subtitle mb-3">Compensation Structure</h4>
                <div className="datagrid">
                  <div className="datagrid-item">
                    <div className="datagrid-title">Percentage</div>
                    <div className="datagrid-content fw-bold text-primary">
                      {office.compensation.percentage}%
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Flat Fee</div>
                    <div className="datagrid-content fw-bold text-primary">
                      ${office.compensation.flatFee.toLocaleString()}
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Minimum Comp</div>
                    <div className="datagrid-content">
                      ${office.compensation.minComp.toLocaleString()}
                    </div>
                  </div>
                  <div className="datagrid-item">
                    <div className="datagrid-title">Maximum Comp</div>
                    <div className="datagrid-content">
                      ${office.compensation.maxComp.toLocaleString()}
                    </div>
                  </div>
                </div>

                <hr className="my-3" />

                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="card-subtitle mb-0">Assigned Users</h4>
                    <div className="text-secondary small">{officeUsers.length} {officeUsers.length === 1 ? 'user' : 'users'}</div>
                  </div>
                  <div>
                    {officeUsers.slice(0, 3).map(user => (
                      <span key={user.id} className="avatar avatar-sm me-1" title={user.name}>
                        {user.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    ))}
                    {officeUsers.length > 3 && (
                      <span className="avatar avatar-sm">+{officeUsers.length - 3}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="card-footer">
                <button className="btn w-100" onClick={() => openEditModal(office)}>
                  <i className="ti ti-edit me-2"></i>
                  Edit Office
                </button>
              </div>
            </div>
          </div>
        );
        })}
      </div>

      {/* Add Office Modal */}
      {showAddModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Office</h5>
                <button type="button" className="btn-close" onClick={() => { setShowAddModal(false); resetForm(); }}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-8">
                    <label className="form-label required">Office Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="e.g., Main Office - Los Angeles"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label required">NMLS ID</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="123456"
                      value={formData.nmls}
                      onChange={(e) => handleChange('nmls', e.target.value)}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label required">Address</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="123 Main St, Suite 100, Los Angeles, CA 90001"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Phone</label>
                  <input 
                    type="tel" 
                    className="form-control" 
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-check form-switch">
                    <input 
                      className="form-check-input" 
                      type="checkbox"
                      checked={formData.isMain}
                      onChange={(e) => handleChange('isMain', e.target.checked)}
                      disabled={hasMainOffice && !formData.isMain}
                    />
                    <span className="form-check-label">Designate as Main Office</span>
                  </label>
                  {hasMainOffice && !formData.isMain && (
                    <div className="text-secondary small">
                      You already have a main office. Only one office can be designated as main.
                    </div>
                  )}
                </div>
                <div className="alert alert-info">
                  <i className="ti ti-info-circle me-2"></i>
                  Default compensation structure will be applied. You can customize it later.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => { setShowAddModal(false); resetForm(); }}>Cancel</button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={handleAddOffice}
                  disabled={!formData.name || !formData.nmls || !formData.address}
                >
                  Create Office
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAddModal && <div className="modal-backdrop fade show"></div>}

      {/* Edit Office Modal */}
      {showEditModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Office</h5>
                <button type="button" className="btn-close" onClick={() => { setShowEditModal(false); setSelectedOffice(null); resetForm(); }}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-8">
                    <label className="form-label required">Office Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="e.g., Main Office - Los Angeles"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label required">NMLS ID</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="123456"
                      value={formData.nmls}
                      onChange={(e) => handleChange('nmls', e.target.value)}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label required">Address</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="123 Main St, Suite 100, Los Angeles, CA 90001"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Phone</label>
                  <input 
                    type="tel" 
                    className="form-control" 
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-check form-switch">
                    <input 
                      className="form-check-input" 
                      type="checkbox"
                      checked={formData.isMain}
                      onChange={(e) => handleChange('isMain', e.target.checked)}
                      disabled={selectedOffice?.isMain || (hasMainOffice && !formData.isMain)}
                    />
                    <span className="form-check-label">Designate as Main Office</span>
                  </label>
                  {selectedOffice?.isMain && (
                    <div className="text-secondary small">
                      This is your main office. You must have at least one main office.
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => { setShowEditModal(false); setSelectedOffice(null); resetForm(); }}>Cancel</button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={handleEditOffice}
                  disabled={!formData.name || !formData.nmls || !formData.address}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showEditModal && <div className="modal-backdrop fade show"></div>}
    </div>
  );
};

export default BrokerOfficesPage;
